import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Dialog } from '@headlessui/react';

/**
 * Reusable modal table for picking items from a cloud-backed list.
 * Props:
 * - open: boolean
 * - onClose: fn()
 * - title: string
 * - columns: [{ key, header, width?, render?(row), sortable?: boolean }]
 * - fetchPage: async ({ limit, offset, search }) => { items, limit, offset }
 * - onPick: fn(row)
 * - onDelete?: fn(row) — optional. When provided, renders a Delete button.
 * - onDuplicate?: fn(row) — optional. When provided, renders a Duplicate button.
 *   The handler should perform the copy and return when done. The dialog will
 *   refetch the table so the new entry is visible.
 * - pageSize?: number (default 25)
 */
export default function CloudTableDialog({ open, onClose, title, columns, fetchPage, onPick, onDelete, onDuplicate, pageSize = 25 }) {
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [lastPage, setLastPage] = useState(false);
  const searchRef = useRef(null);

  const doFetch = async () => {
    setIsLoading(true); setError('');
    try {
      const res = await fetchPage({ limit: pageSize, offset, search });
      const items = res?.items || [];
      setRows(items);
      setLastPage(items.length < pageSize);
      if (items.length > 0) setSelectedIndex(0); else setSelectedIndex(-1);
    } catch (e) {
      setError(e.message || 'Failed to load');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    doFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, offset]);

  const onSearch = () => { setOffset(0); doFetch(); };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'Escape') onClose?.();
    if (e.key === 'ArrowDown') { setSelectedIndex(i => Math.min(i + 1, rows.length - 1)); e.preventDefault(); }
    if (e.key === 'ArrowUp') { setSelectedIndex(i => Math.max(i - 1, 0)); e.preventDefault(); }
    if (e.key === 'Enter') { if (rows[selectedIndex]) onPick(rows[selectedIndex]); }
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => handleKeyDown(e);
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rows, selectedIndex]);

  return (
    <Dialog open={open} onClose={onClose} initialFocus={searchRef}>
      {/* Centering layer */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* Backdrop */}
        <div aria-hidden="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)' }} />
        {/* Panel */}
        <Dialog.Panel style={{ position: 'relative', background: '#fff', borderRadius: 6, boxShadow: '0 10px 30px rgba(0,0,0,0.2)', width: 860, maxWidth: '95vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: 12, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Dialog.Title style={{ fontWeight: 600, fontSize: 16, flex: 1 }}>{title}</Dialog.Title>
            <input
              ref={searchRef}
              placeholder="Search by name or ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e)=>{ if (e.key==='Enter') onSearch(); }}
              style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, minWidth: 240 }}
            />
            <button onClick={onSearch} className="btn-small" style={{ padding: '6px 10px' }}>Search</button>
          </div>
          {error && (
            <div style={{ color: '#8b0000', background: '#ffe8e8', borderBottom: '1px solid #ffb8b8', padding: 8, fontSize: 12 }}>{error}</div>
          )}
          <div style={{ overflow: 'auto', flex: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, background: '#fafafa' }}>
                <tr>
                  {columns.map(col => (
                    <th key={col.key} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #eee', width: col.width }}>{col.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={columns.length} style={{ padding: 12, fontSize: 13, color: '#666' }}>Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={columns.length} style={{ padding: 12, fontSize: 13, color: '#666' }}>No results</td></tr>
                ) : rows.map((row, idx) => (
                  <tr key={row.id || row.survey_id || idx}
                      onDoubleClick={()=>onPick(row)}
                      onClick={()=>setSelectedIndex(idx)}
                      style={{ background: idx===selectedIndex ? '#eef5ff' : 'transparent', cursor: 'pointer' }}>
                    {columns.map(col => (
                      <td key={col.key} style={{ padding: '8px 10px', borderBottom: '1px solid #f2f2f2', fontSize: 13 }}>
                        {col.render ? col.render(row) : String(row[col.key] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', padding: 10, borderTop: '1px solid #eee' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button disabled={offset===0} onClick={()=> setOffset(Math.max(0, offset - pageSize))} className="btn-small" style={{ padding: '6px 10px' }}>Previous</button>
              <button disabled={lastPage} onClick={()=> setOffset(offset + pageSize)} className="btn-small" style={{ padding: '6px 10px' }}>Next</button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {onDelete && (
                <button
                  disabled={selectedIndex<0 || rows.length===0}
                  onClick={async()=>{
                    const row = rows[selectedIndex];
                    if (!row) return;
                    try {
                      const result = await onDelete(row);
                      if (result !== false) await doFetch();
                    } catch (_) {}
                  }}
                  className="btn-small"
                  style={{ padding: '6px 10px', background: '#d9534f', color: '#fff', border: 'none', borderRadius: 4 }}
                >
                  Delete
                </button>
              )}
              {onDuplicate && (
                <button
                  disabled={selectedIndex<0 || rows.length===0}
                  onClick={async()=>{
                    const row = rows[selectedIndex];
                    if (!row) return;
                    try {
                      const result = await onDuplicate(row);
                      // Refetch the table so the new copy shows up, unless
                      // the handler explicitly returned false (e.g. it errored
                      // and showed its own toast and wants to leave state alone).
                      if (result !== false) await doFetch();
                    } catch (_) {}
                  }}
                  className="btn-small"
                  style={{ padding: '6px 10px' }}
                >
                  Duplicate
                </button>
              )}
              <button onClick={onClose} className="btn-small" style={{ padding: '6px 10px' }}>Cancel</button>
              <button disabled={selectedIndex<0 || rows.length===0} onClick={()=> rows[selectedIndex] && onPick(rows[selectedIndex])} className="btn-small" style={{ padding: '6px 12px', background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 4 }}>Open</button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}


